import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from 'components/preloader/preloader.module.css';
import { IS_MOBILE } from 'constants/responsive';
import { ImgBackground } from 'sections/heroPage/hero/ImgBackground';

interface VideoLoaderProps {
    setIsVideoLoaded: (_v: boolean) => void;
    isVideoLoaded: boolean;
    children: JSX.Element;
}

export const Preloader: React.FC<VideoLoaderProps> = ({
    setIsVideoLoaded,
    isVideoLoaded,
    children,
}) => {
    const videoUrl =
        'https://femme-general.s3.eu-north-1.amazonaws.com/home/intro.mp4';

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [loadedPercentage, setLoadedPercentage] = useState<number>(0);
    const [canPlayThrough, setCanPlayThrough] = useState<boolean>(false);
    const [visibleElement, setVisibleElement] = useState(1);

    const { t } = useTranslation();

    const fullyLoadedVideoPercent = 100;
    const delayBeforeVideoShow = 1000;
    const delayToShowMainImgBg = 1200;
    const delayToImulateVideoLoaded = 400;
    const preloaderTextDisplayDelay = 400;

    useEffect(() => {
        const video = videoRef.current;

        const handleProgress = () => {
            if (video && video.buffered.length > 0) {
                const bufferedEnd = video.buffered.end(
                    video.buffered.length - 1
                );
                const duration = video.duration;

                if (duration > 0) {
                    setLoadedPercentage((prev) => {
                        const newPercentage = (bufferedEnd / duration) * 100;
                        return prev !== fullyLoadedVideoPercent
                            ? newPercentage
                            : fullyLoadedVideoPercent;
                    });
                }
            }
        };

        const handleCanPlayThrough = () => {
            setCanPlayThrough(true);
            setLoadedPercentage(fullyLoadedVideoPercent);
        };

        if (video) {
            video.addEventListener('progress', handleProgress);
            video.addEventListener('canplaythrough', handleCanPlayThrough);

            return () => {
                video.removeEventListener('progress', handleProgress);
                video.removeEventListener(
                    'canplaythrough',
                    handleCanPlayThrough
                );
            };
        }
    }, [loadedPercentage]);

    useEffect(() => {
        const shouldShowAVideo =
            loadedPercentage === fullyLoadedVideoPercent && canPlayThrough;

        if (shouldShowAVideo) {
            const timerForText = setTimeout(() => {
                setVisibleElement(2);
            }, preloaderTextDisplayDelay);

            const timeout = setTimeout(() => {
                setIsVideoLoaded(true);
            }, delayBeforeVideoShow);

            return () => {
                clearTimeout(timerForText);
                clearTimeout(timeout);
            };
        }
    }, [loadedPercentage, canPlayThrough, setIsVideoLoaded]);

    useEffect(() => {
        if (IS_MOBILE) {
            const timeout = setTimeout(() => {
                setCanPlayThrough(true);
                setLoadedPercentage(fullyLoadedVideoPercent);
            }, delayToImulateVideoLoaded);

            const timerForMainContent = setTimeout(() => {
                setIsVideoLoaded(true);
            }, delayToShowMainImgBg);

            return () => {
                clearTimeout(timerForMainContent);
                clearTimeout(timeout);
            };
        }
    }, [setIsVideoLoaded]);

    return (
        <div className={styles.videoBackground}>
            {!isVideoLoaded && (
                <div className={styles.loaderContainer}>
                    {visibleElement === 1 && (
                        <div className={styles.progressBar__container}>
                            <div
                                className={styles.progressBar}
                                style={{
                                    width: `${loadedPercentage}%`,
                                }}
                            ></div>
                        </div>
                    )}

                    {visibleElement === 2 && (
                        <h2 className={styles.loaderText}>
                            {t('hero.dareToDiffer')}
                        </h2>
                    )}
                </div>
            )}

            {!IS_MOBILE ? (
                <video
                    ref={videoRef}
                    muted
                    autoPlay
                    loop
                    className={styles.video}
                >
                    <source src={videoUrl} type="video/mp4" />
                    <img
                        src="https://femme-general.s3.eu-north-1.amazonaws.com/home/introImg.jpg"
                        alt="show ballet"
                    />
                </video>
            ) : (
                <ImgBackground />
            )}

            {isVideoLoaded && children}
        </div>
    );
};
